import React from 'react';
import { COVERS } from '../data/pexels';
import { MiniAvatar } from './MiniApp';
import { useLocale } from '../i18n/LocaleContext';

type Ribbon = { kind: 'full' } | { kind: 'left'; n: number };
type TFn = (key: string) => string;

const ribbonText = (r: Ribbon, t: TFn): string => {
  if (r.kind === 'full') return t('mq.full');
  if (r.n === 1) return t('mq.placeLeft');
  return t('mq.placesLeft').replace('{n}', String(r.n));
};

interface Person {
  first: string;
  last: string;
}

// A small cast of people reused across the demo events. Avatar photos are
// resolved from the name by MiniAvatar, so faces stay consistent site-wide.
const P: Record<string, Person> = {
  mia: { first: 'Mia', last: 'K.' },
  tom: { first: 'Tom', last: 'B.' },
  ana: { first: 'Ana', last: 'D.' },
  sam: { first: 'Sam', last: 'R.' },
  jan: { first: 'Jan', last: 'K.' },
  lia: { first: 'Lia', last: 'P.' },
  ivo: { first: 'Ivo', last: 'S.' },
  nina: { first: 'Nina', last: 'H.' },
};

interface MqEvent {
  cover: string;
  name: string;
  date: string;
  fee?: string;
  city: string;
  club?: string;
  host: Person;
  ribbon?: Ribbon;
  guests: Person[];
}

// Demo events on the R2 cover art, mirroring the mobile EventCard layout.
// Guest counts vary (0–3) so the strip feels real.
const EVENTS: MqEvent[] = [
  { cover: COVERS.ghosts, name: 'Ghost drive-in movie', date: 'Fri · 21:00', fee: '€8', city: 'Košice', club: 'Night Owls', host: P.mia, ribbon: { kind: 'left', n: 3 }, guests: [P.tom, P.lia, P.sam] },
  { cover: COVERS.jukebox, name: 'Sunset vinyl rooftop', date: 'Tonight · 19:00', city: 'Košice', club: 'Golden Hour', host: P.tom, guests: [P.ana, P.jan] },
  { cover: COVERS.sunset, name: 'Synthwave sundown', date: 'Sat · 20:30', fee: '€12', city: 'Bratislava', host: P.ana, ribbon: { kind: 'full' }, guests: [P.mia, P.sam, P.nina] },
  { cover: COVERS.diner, name: 'Late-night diner run', date: 'Thu · 23:00', city: 'Prague', host: P.sam, guests: [] },
  { cover: COVERS.car, name: 'Neon night drive', date: 'Sat · 22:00', fee: '€5', city: 'Vienna', club: 'Cruisers', host: P.jan, guests: [P.ana] },
  { cover: COVERS.cat, name: 'Cosmic cat lounge', date: 'Sun · 17:00', fee: '€10', city: 'Košice', host: P.lia, ribbon: { kind: 'left', n: 1 }, guests: [P.nina, P.tom, P.sam] },
  { cover: COVERS.palms, name: 'Palm district social', date: 'Wed · 18:30', city: 'Brno', club: 'Sunday Runners', host: P.ivo, guests: [P.mia, P.ana] },
  { cover: COVERS.beach, name: 'Sunrise beach jam', date: 'Sun · 06:30', fee: '€6', city: 'Split', host: P.nina, guests: [] },
];

const Dot = (): React.JSX.Element => <span className="ev-dot" />;

const EventCard = ({ e, t }: { e: MqEvent; t: TFn }): React.JSX.Element => (
  <div className="mq-item ev-card">
    <img className="ev-img" src={e.cover} alt={e.name} width={300} height={300} />
    <div className="ev-top-grad" />
    <div className="ev-bottom-grad" />

    {e.ribbon ? <div className="ev-ribbon">{ribbonText(e.ribbon, t)}</div> : null}

    <div className="ev-top">
      <MiniAvatar first={e.host.first} last={e.host.last} size={35} />
      {e.club ? (
        <>
          <span className="ev-author">{e.club}</span>
          <Dot />
        </>
      ) : null}
      <span className="ev-author">
        {e.host.first} {e.host.last}
      </span>
    </div>

    <div className="ev-content">
      <div className="ev-title">{e.name}</div>
      <div className="ev-meta">
        <span>{e.date}</span>
        {e.fee ? (
          <>
            <Dot />
            <span>{e.fee}</span>
          </>
        ) : null}
        <Dot />
        <span>{e.city}</span>
      </div>
      <div className="ev-actions">
        <div className="ev-btn">{t('mq.view')}</div>
        {e.guests.length > 0 ? (
          <div className="ev-first-three">
            {e.guests.slice(0, 3).map((g, i) => (
              <MiniAvatar key={i} first={g.first} last={g.last} size={25} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  </div>
);

export const PhotoMarquee = (): React.JSX.Element => {
  const { t } = useLocale();
  const Group = (): React.JSX.Element => (
    <div className="mq-group">
      {EVENTS.map((e) => (
        <EventCard key={e.name} e={e} t={t} />
      ))}
    </div>
  );

  return (
    <section className="photo-marquee" aria-label="Events on Gather">
      <div className="mq-head">
        <span className="eyebrow">{t('mq.eyebrow')}</span>
        <h2>{t('mq.h')}</h2>
      </div>
      <div className="mq-viewport">
        <div className="mq-track">
          <Group />
          <Group />
        </div>
      </div>
    </section>
  );
};
